import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Chip, CircularProgress, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Translate } from 'react-localize-redux';
import { IField, IPatient, ISubmission, ISurvey } from '../../../constants/types';
import { BillingInfo } from '../Billing/types';
import { formatSubmissionDateTime } from '../../../utils/dateUtils';
import SubmissionsTable, { CaseStatusChip } from './SubmissionsTable';
import { buildVisitGroups, pickRichestSubmissionIndex, resolveExpandedKeys, VisitShell } from './visitExpansion';
import VisitStatusAction from './VisitStatusAction';
import { OtherOpenDraft, VisitStatusChange } from './visitStatusChange';

const NO_VISIT_KEY = '__no_visit__';

// Stable default so an omitted `shellVisits` prop doesn't hand a fresh array
// identity to the buildVisitGroups useMemo on every render — that churn re-fired
// the expand-keys effect and looped setState ("Maximum update depth exceeded").
const EMPTY_SHELL_VISITS: VisitShell[] = [];

/**
 * SKIN-38 — Visit-grouped submissions view.
 *
 * REPLACES the flat SubmissionsTable when an investigation has FUNCTIONALITY.CASES
 * and the submissions carry a visit. Groups the flat submissions array by visit and
 * renders one accordion per visit: header = visit date + status chip, body = the
 * existing SubmissionsTable scoped to that visit's submissions.
 *
 * SKIN-55 — also renders visit shells from the case API (visits with no submissions
 * yet) and prefers expanding the visit that has clinical data over empty DRAFT shells.
 */

interface VisitGroupedSubmissionsTableProps {
    locale: string;
    fieldsSurvey?: IField[];
    surveys: ISurvey[];
    records: ISubmission[];
    category: number;
    patient: IPatient;
    billingInfo: BillingInfo | null;
    loading?: boolean;
    onSelectSubmission: (index: number) => void;
    /** Visits on the patient's case that may not have submissions yet. */
    shellVisits?: VisitShell[];
    /** SKIN-55 — when set (e.g. from ?visitUuid=), auto-expand that visit accordion. */
    initialVisitUuid?: string | null;
    /** Called when the user opens/closes a visit so the parent can sync ?visitUuid= in the URL. */
    onVisitUuidChange?: (visitUuid: string | null) => void;
    /**
     * SKIN-119 — when true, each visit renders a single row: the submission with the
     * most filled fields. Gated per-tenant (FUNCTIONALITY.CLINICAL) by the parent.
     */
    collapseToRichestSubmission?: boolean;
    /**
     * SKIN-543 — close / reopen a visit from its header. Passed only when the reviewer
     * may (APPROVE_VISIT + FUNCTIONALITY.WEB_VISIT_STATUS); absent, no action is shown.
     */
    onChangeVisitStatus?: (visitUuid: string, change: VisitStatusChange, reason: string) => Promise<unknown>;
    /** SKIN-543 — the visit author's other open drafts on the case, read when the reopen dialog opens. */
    loadOtherOpenDrafts?: (visitUuid: string) => Promise<OtherOpenDraft[]>;
}

/** The newest submission of a visit: what the "a phone may still hold data" warning reads. */
function lastSubmissionAt(records: ISubmission[], indices: number[]): Date | null {
    const times = indices.map((i) => new Date(records[i]?.createdAt).getTime()).filter((t) => !Number.isNaN(t));
    return times.length > 0 ? new Date(Math.max(...times)) : null;
}

const VisitGroupedSubmissionsTable: React.FC<VisitGroupedSubmissionsTableProps> = ({
    locale, fieldsSurvey, surveys, records, category, patient, billingInfo, loading = false, onSelectSubmission,
    shellVisits = EMPTY_SHELL_VISITS,
    initialVisitUuid,
    onVisitUuidChange,
    collapseToRichestSubmission = false,
    onChangeVisitStatus,
    loadOtherOpenDrafts,
}) => {
    const groups = useMemo(
        () => buildVisitGroups(records, shellVisits),
        [records, shellVisits],
    );
    const visitAccordionRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => resolveExpandedKeys(groups, initialVisitUuid));

    useEffect(() => {
        setExpandedKeys(resolveExpandedKeys(groups, initialVisitUuid));
    }, [groups, initialVisitUuid]);

    useEffect(() => {
        if (!initialVisitUuid) return;
        const node = visitAccordionRefs.current[initialVisitUuid];
        if (node) {
            node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [initialVisitUuid, groups]);

    function toggleVisit(key: string, isExpanded: boolean) {
        if (isExpanded) {
            setExpandedKeys(new Set([key]));
            onVisitUuidChange?.(key === NO_VISIT_KEY ? null : key);
            return;
        }
        setExpandedKeys(new Set());
        if (initialVisitUuid === key) {
            onVisitUuidChange?.(null);
        }
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (groups.length === 0) {
        return (
            <Typography variant="body2" color="textSecondary" style={{ padding: '1rem' }}>
                <Translate id="pages.hospital.patient.no-visits-with-data" />
            </Typography>
        );
    }

    return (
        <Box>
            {groups.map((group) => {
                const richest = collapseToRichestSubmission ? pickRichestSubmissionIndex(records, group.indices) : null;
                const collapsed = richest !== null;
                const displayIndices = collapsed ? [richest] : group.indices;
                const visitRecords = displayIndices.map((i) => records[i]);
                const countLabel = collapsed && group.indices.length > 1
                    ? `1 / ${group.indices.length}`
                    : group.indices.length;
                const dateLabel = group.visitDate ? formatSubmissionDateTime(group.visitDate) : '';
                return (
                    <Accordion
                        key={group.key}
                        ref={(node) => { visitAccordionRefs.current[group.key] = node; }}
                        // SKIN-543 e2e: the header text (date, status) changes with the status itself
                        data-testid="visit-accordion"
                        data-visit-uuid={group.key === NO_VISIT_KEY ? undefined : group.key}
                        expanded={expandedKeys.has(group.key)}
                        onChange={(_e, isExpanded) => toggleVisit(group.key, isExpanded)}
                        TransitionProps={{ unmountOnExit: true }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" gap={1.5} width="100%" flexWrap="wrap">
                                <Typography variant="subtitle2">
                                    <Translate id="pages.hospital.patient.visit" />
                                    {dateLabel ? ` · ${dateLabel}` : ''}
                                </Typography>
                                {group.visitStatus && <CaseStatusChip status={group.visitStatus} />}
                                <Chip size="small" variant="outlined" label={countLabel} />
                                {onChangeVisitStatus && group.key !== NO_VISIT_KEY && (
                                    <VisitStatusAction
                                        status={group.visitStatus}
                                        lastSubmissionAt={lastSubmissionAt(records, group.indices)}
                                        onChangeStatus={(change, reason) => onChangeVisitStatus(group.key, change, reason)}
                                        loadOtherOpenDrafts={loadOtherOpenDrafts && (() => loadOtherOpenDrafts(group.key))}
                                        onJumpToMissingField={(field) => {
                                            // Open the submission the API says the field is missing from;
                                            // the client's richest is only a fallback, it ranks differently.
                                            const named = group.indices.find((i) => records[i]?.id === field.submissionId);
                                            const target = named ?? pickRichestSubmissionIndex(records, group.indices);
                                            if (target !== null && target !== undefined) onSelectSubmission(target);
                                        }}
                                    />
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails style={{ display: 'block', padding: 0 }}>
                            {group.indices.length === 0 ? (
                                <Typography variant="body2" color="textSecondary" sx={{ px: 2, py: 1.5 }}>
                                    <Translate id="pages.hospital.patient.empty-visit-hint" />
                                </Typography>
                            ) : (
                                <SubmissionsTable
                                    locale={locale}
                                    fieldsSurvey={fieldsSurvey}
                                    surveys={surveys}
                                    records={visitRecords}
                                    category={category}
                                    patient={patient}
                                    billingInfo={billingInfo}
                                    casesEnabled={false}
                                    onSelectSubmission={(localIndex: number) => onSelectSubmission(displayIndices[localIndex])}
                                />
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

export default VisitGroupedSubmissionsTable;
